import { faTelescope } from '@fortawesome/pro-duotone-svg-icons';
import moment from 'moment';
import React, { FC } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { EmptyCell } from 'components/atoms/empty-cell';
import {
  EmptyState,
  EmptyStateContainer as EmptyStateContainerAtom,
  EmptyStateIcon,
} from 'components/atoms/empty-state';
import { HumanisedDate } from 'components/atoms/humanised-date';
import { IconShell } from 'components/atoms/icon-shell';
import { Tag } from 'components/atoms/tag';
import { TemplateObjectType } from 'data/templates/api';
import { Translate } from 'hooks/use-translation';
import { fullName } from 'utils/full-name';
import { Bullhorn, Document, FormTemplateIcon, RoadMap } from '../styles';
import { TemplateCardMoreActions } from '../template-card-more-actions';
import { TemplateListProp } from '../types';
import { TemplateCellRendererParams } from './table-utils';

export const Icons = {
  [TemplateObjectType.Project]: <RoadMap />,
  [TemplateObjectType.Document]: <Document />,
  [TemplateObjectType.StatusUpdates]: <Bullhorn />,
  [TemplateObjectType.Form]: <FormTemplateIcon />,
};

const ClickableName = styled.div`
  cursor: pointer;
  overflow: hidden;
  height: 20px;
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  white-space: nowrap;
`;
const UserName = styled.div`
  overflow: auto;
  height: 20px;
`;

export enum NameType {
  CreatedBy = 'CREATED_BY',
  CreatedAt = 'CREATED_AT',
  PublishedBy = 'PUBLISHED_BY',
  PublishedAt = 'PUBLISHED_AT',
}

interface TemplateCellParams extends TemplateCellRendererParams {
  translate: Translate;
  type: NameType;
}

export const getAvatar = (objectType: TemplateObjectType): string => {
  return renderToStaticMarkup(
    <IconShell icon={Icons[objectType]} size="xsmall" />,
  );
};

export const getTemplateName = ({
  data,
  translate,
}: TemplateCellParams): string => {
  return renderToStaticMarkup(
    <ClickableName data-cy={data.templateName}>
      <span>{data.templateName}</span>

      {data.enableAutoPublish === true ? (
        <Tag color="purple">{translate('auto_published')}</Tag>
      ) : null}
    </ClickableName>,
  );
};

type TemplateOptionsRenderer = {
  data: TemplateListProp;
  objectType: TemplateObjectType;
  onDelete: (id, objectType: TemplateObjectType) => void;
  onDuplicate?: (id, objectType: TemplateObjectType) => void;
};

export const TemplateOptionsRenderer: FC<TemplateOptionsRenderer> = ({
  data,
  objectType,
  onDelete,
  onDuplicate,
}: TemplateOptionsRenderer) => {
  return (
    <TemplateCardMoreActions
      objectType={objectType}
      template={data}
      onDelete={onDelete}
      onDuplicate={onDuplicate}
    />
  );
};

export const getUserName = ({ data, type }: TemplateCellParams): string => {
  const name = type === NameType.CreatedBy ? data.createdBy : data.publishedBy;

  return renderToStaticMarkup(
    name ? <UserName>{name && fullName(name)}</UserName> : <EmptyCell />,
  );
};

export const getDate = ({ data, type }: TemplateCellParams): string => {
  const time = type === NameType.CreatedAt ? data.createdAt : data.publishedAt;

  return renderToStaticMarkup(
    time ? <HumanisedDate timestamp={moment(time)} /> : <EmptyCell />,
  );
};

const EmptyStateContainer = styled(EmptyStateContainerAtom)`
  && {
    padding-top: var(--spacing-08);
    padding-bottom: var(--spacing-08);
  }
`;

export const NoResultTemplateListEmptyState: FC = () => {
  return (
    <EmptyStateContainer>
      <EmptyState
        image={<EmptyStateIcon icon={faTelescope} />}
        title={<FormattedMessage id="template.no_results.empty.title" />}
        message={<FormattedMessage id="template.no_results.empty.message" />}
        secondaryCTA={null}
      />
    </EmptyStateContainer>
  );
};
